import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { 
  Briefcase, 
  Target, 
  Clock, 
  Scissors, 
  Zap,
  TrendingUp,
  DollarSign,
  BarChart,
  Users
} from 'lucide-react';

// Dynamic data arrays
const demographicsData = [
  { name: 'New Users', value: 40, color: '#2C7A7B' }, // Teal
  { name: 'Retained Users', value: 35, color: '#4C51BF' }, // Blue/Purple
  { name: 'Reactivated Users', value: 15, color: '#B7791F' }, // Gold
  { name: 'Churned', value: 10, color: '#702459' } // Magenta
];

const metricsData = [
  {
    value: '200%',
    label: 'User Engagement',
    icon: TrendingUp,
    color: '#E2E8F0' // Light color for icons
  },
  {
    value: '30%',
    label: 'Monthly Revenue',
    icon: DollarSign,
    color: '#E2E8F0'
  },
  {
    value: '2X',
    label: 'Session Time',
    icon: Clock,
    color: '#E2E8F0'
  },
  {
    value: '90%',
    label: 'Cost Reduction',
    icon: Scissors,
    color: '#E2E8F0'
  },
  {
    value: '50%',
    label: 'Organic Traffic',
    icon: BarChart,
    color: '#E2E8F0'
  },
  {
    value: '80%',
    label: 'Manual Work',
    icon: Zap,
    color: '#E2E8F0'
  }
];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
  background: transparent;
  z-index: 10;
  position: relative;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

// Titles
const TitleBlock = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;
`;

const Title = styled.h2`
  font-size: 38px;
  font-weight: 800;
  color: #FFFFFF;
  margin: 0;
  letter-spacing: -0.02em;
`;

const SubText = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

// Top Stats
const TopStats = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const TopStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 8px;
`;

// Pie Chart Card
const ChartCard = styled.div`
  width: 100%;
  max-width: 700px;
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 24px;
  height: 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #FFFFFF;
  margin: 0 0 16px 0;
`;

// Metrics Grid
const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(30, 30, 35, 0.6);
  }
`;

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MetricValue = styled.h3`
  font-size: 32px;
  font-weight: 800;
  color: #FFFFFF;
  margin: 0;
`;

const MetricLabel = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

// Custom Recharts label renderer
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, name }) => {
  const RADIAN = Math.PI / 180;
  // Reduced from 1.35 to 1.15 to keep labels strictly inside the card
  const radius = outerRadius * 1.15; 
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="#FFFFFF" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize="13px"
    >
      {`${name} (${value}%)`}
    </text>
  );
};

const ImpactPortfolio = () => {
  return (
    <Container id="analytics">
      <Wrapper>
        <TitleBlock
           as={motion.div}
           initial={{ opacity: 0, y: 20 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ once: true }}
        >
          <Title>Impact & Growth Portfolio</Title>
          <SubText>Key metrics and visualization of product performance.</SubText>
        </TitleBlock>

        <TopStats
           as={motion.div}
           initial={{ opacity: 0, y: 10 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ once: true }}
           transition={{ delay: 0.1 }}
        >
          <TopStatItem><Briefcase size={14} /> 1+ Years Experience</TopStatItem>
          <Dot />
          <TopStatItem><Target size={14} /> 500K+ Users Reached</TopStatItem>
          <Dot />
          <TopStatItem><Zap size={14} /> 8+ AI Products Shipped</TopStatItem>
          <Dot />
          <TopStatItem><Users size={14} /> 10+ Clients Served</TopStatItem>
        </TopStats>

        {/* Dynamic Demographics Chart */}
        <ChartCard
           as={motion.div}
           initial={{ opacity: 0, scale: 0.98 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           transition={{ delay: 0.2 }}
        >
          <ChartTitle>User Demographics</ChartTitle>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
               <Tooltip 
                 contentStyle={{ 
                   background: 'rgba(15, 15, 20, 0.9)', 
                   border: '1px solid rgba(255,255,255,0.1)',
                   borderRadius: '8px',
                   color: '#fff'
                 }}
                 itemStyle={{ color: '#fff' }}
               />
               <Pie
                 data={demographicsData}
                 cx="50%"
                 cy="50%"
                 innerRadius="45%"
                 outerRadius="75%"
                 paddingAngle={2}
                 dataKey="value"
                 label={renderCustomizedLabel}
                 labelLine={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1, length1: 10, length2: 5 }}
                 isAnimationActive={true}
               >
                 {demographicsData.map((entry, index) => (
                   <Cell key={`cell-${index}`} fill={entry.color} />
                 ))}
               </Pie>
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Dynamic 6-Card Grid */}
        <MetricsGrid>
          {metricsData.map((metric, idx) => (
            <MetricCard 
              key={idx}
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + (idx * 0.05) }}
            >
              <ValueRow>
                <metric.icon size={20} color={metric.color} opacity={0.7} />
                <MetricValue>{metric.value}</MetricValue>
              </ValueRow>
              <MetricLabel>{metric.label}</MetricLabel>
            </MetricCard>
          ))}
        </MetricsGrid>

      </Wrapper>
    </Container>
  );
};

export default ImpactPortfolio;
